// ─── TermsPage ────────────────────────────────────────────────────────────────
// Terms of Service placeholder for SafetyQR.
// IMPORTANT: This document contains placeholder content. Have it reviewed and
// finalised by a qualified Indian lawyer before enforcement.

export function TermsPage() {
  return (
    <div className="min-h-svh px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-12">

        {/* ── Header ── */}
        <div className="space-y-4">
          <a href="/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            ← Back to SafetyQR
          </a>
          <p className="shell-kicker text-xs font-semibold tracking-[0.18em] uppercase text-primary">
            Legal
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight">
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground leading-6">
            Last updated: March 2026 &nbsp;·&nbsp; Effective date: March 2026
          </p>
          <div className="rounded-3xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900">
            <strong>Note:</strong> These Terms of Service are a structured placeholder
            currently under legal review. They will be updated with finalised language
            before full enforcement. If you have questions, contact us at{" "}
            <a href="mailto:support@feedbacknfc.com" className="underline">
              support@feedbacknfc.com
            </a>.
          </div>
        </div>

        <Divider />

        {/* ── 1. About SafetyQR ── */}
        <Section number="1" title="About SafetyQR">
          <p>
            SafetyQR is a product of <strong>FeedbackNFC</strong> ("Company", "we", "our",
            "us"). SafetyQR provides physical QR sticker kits that, when scanned, allow a
            bystander, first responder, or good Samaritan to notify your designated
            emergency contacts and share your approximate location.
          </p>
          <p className="mt-3">
            By requesting a SafetyQR kit, registering your QR sticker, or using any part
            of the SafetyQR platform at{" "}
            <a href="https://traffic.feedbacknfc.com" className="text-primary hover:underline">
              traffic.feedbacknfc.com
            </a>
            , you agree to be bound by these Terms of Service.
          </p>
        </Section>

        {/* ── 2. Eligibility ── */}
        <Section number="2" title="Eligibility">
          <p>You may use SafetyQR if:</p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li>You are 18 years of age or older, OR</li>
            <li>
              You are a parent or legal guardian registering on behalf of a person under
              18 (including Students SafetyQR). By registering for a minor, you confirm
              you have the authority to consent on their behalf and agree to these Terms
              on their behalf.
            </li>
          </ul>
          <p className="mt-3">
            SafetyQR is intended for use within India. Use outside India may not be
            supported and is at your own risk.
          </p>
        </Section>

        {/* ── 3. How the Service Works ── */}
        <Section number="3" title="How the Service Works">
          <ol className="mt-3 space-y-3 list-decimal list-outside pl-5">
            <li>
              <strong>Kit Request:</strong> You request a SafetyQR kit by filling out our
              online form. Our team reviews your request and arranges delivery.
            </li>
            <li>
              <strong>Sticker Placement:</strong> You place the QR stickers on your
              belongings (bag, helmet, vehicle, etc.) as instructed with your kit.
            </li>
            <li>
              <strong>Registration:</strong> You register your sticker at{" "}
              <a
                href="https://traffic.feedbacknfc.com/register"
                className="text-primary hover:underline"
              >
                traffic.feedbacknfc.com/register
              </a>{" "}
              and provide your emergency contact details.
            </li>
            <li>
              <strong>Emergency Use:</strong> If someone finds you in distress, they scan
              the QR code. This opens a report form and sends an alert to your emergency
              contact(s).
            </li>
          </ol>
        </Section>

        {/* ── 4. Your Responsibilities ── */}
        <Section number="4" title="Your Responsibilities">
          <p>By using SafetyQR, you agree to:</p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li>Provide accurate and up-to-date personal and emergency contact information.</li>
            <li>Keep your emergency contact details updated whenever they change.</li>
            <li>Inform your emergency contacts that you have registered them on SafetyQR.</li>
            <li>Not misuse the platform to submit false emergency reports.</li>
            <li>Not attempt to reverse-engineer, hack, or disrupt the SafetyQR platform.</li>
            <li>Comply with all applicable Indian laws while using the service.</li>
          </ul>
        </Section>

        {/* ── 5. Limitation of Liability ── */}
        <Section number="5" title="Limitation of Liability">
          <div className="rounded-3xl border border-border/70 bg-muted/30 px-5 py-4 space-y-3">
            <p>
              <strong>SafetyQR is a safety aid — not a guarantee of emergency response.</strong>
            </p>
            <p>
              We make no warranty, express or implied, that use of a SafetyQR sticker will
              prevent harm, injury, or death, or that emergency contacts will be reached
              in time. The effectiveness of the service depends on factors outside our
              control, including but not limited to: network availability, the scanner's
              device, and the emergency contact's availability.
            </p>
            <p>
              To the maximum extent permitted by applicable Indian law, FeedbackNFC shall
              not be liable for any direct, indirect, incidental, consequential, or special
              damages arising from your use of or inability to use the SafetyQR service,
              including in any emergency situation.
            </p>
          </div>
        </Section>

        {/* ── 6. Kit Delivery ── */}
        <Section number="6" title="Kit Delivery">
          <p>
            Kit requests are subject to availability and serviceability in your area.
            Delivery timelines are estimated and not guaranteed. We will contact you via
            the phone number provided to coordinate delivery. We reserve the right to
            decline kit requests at our discretion.
          </p>
          <p className="mt-3">
            At this time, SafetyQR kits are provided as part of our service offering.
            Pricing, if applicable, will be communicated clearly at the time of request.
          </p>
        </Section>

        {/* ── 7. Intellectual Property ── */}
        <Section number="7" title="Intellectual Property">
          <p>
            All content on the SafetyQR platform, including but not limited to text,
            graphics, logos, icons, and software, is the property of FeedbackNFC and is
            protected under applicable Indian intellectual property laws.
          </p>
          <p className="mt-3">
            You may not reproduce, distribute, modify, or create derivative works of any
            content from SafetyQR without prior written permission from FeedbackNFC.
          </p>
        </Section>

        {/* ── 8. Privacy ── */}
        <Section number="8" title="Privacy">
          <p>
            Your use of SafetyQR is also governed by our{" "}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
            , which is incorporated into these Terms by reference. By using SafetyQR, you
            also agree to the terms of our Privacy Policy.
          </p>
        </Section>

        {/* ── 9. Termination ── */}
        <Section number="9" title="Termination">
          <p>
            You may discontinue your use of SafetyQR at any time by requesting account
            deletion via{" "}
            <a href="mailto:support@feedbacknfc.com" className="text-primary hover:underline">
              support@feedbacknfc.com
            </a>
            . Upon deletion, your personal data will be handled as described in our
            Privacy Policy.
          </p>
          <p className="mt-3">
            We reserve the right to suspend or terminate your access to SafetyQR if you
            violate these Terms or misuse the platform.
          </p>
        </Section>

        {/* ── 10. Changes to Terms ── */}
        <Section number="10" title="Changes to These Terms">
          <p>
            We may update these Terms of Service from time to time. When we do, we will
            update the "Last updated" date at the top of this page. Continued use of
            SafetyQR after any changes constitutes your acceptance of the revised Terms.
            For material changes, we will make reasonable efforts to notify you.
          </p>
        </Section>

        {/* ── 11. Governing Law ── */}
        <Section number="11" title="Governing Law and Jurisdiction">
          <p>
            These Terms are governed by and construed in accordance with the laws of
            India. Any disputes arising from these Terms or your use of SafetyQR shall be
            subject to the exclusive jurisdiction of the courts located in{" "}
            <strong>Bengaluru, Karnataka, India</strong>.
          </p>
        </Section>

        {/* ── 12. Contact ── */}
        <Section number="12" title="Contact Us">
          <p>
            For any questions about these Terms, please contact us:
          </p>
          <div className="mt-3 rounded-3xl border border-border/70 bg-muted/30 px-5 py-4 space-y-1 text-sm">
            <p><strong>FeedbackNFC / SafetyQR</strong></p>
            <p>
              Email:{" "}
              <a href="mailto:support@feedbacknfc.com" className="text-primary hover:underline">
                support@feedbacknfc.com
              </a>
            </p>
          </div>
        </Section>

        <Divider />

        {/* ── Footer nav ── */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <a href="/" className="text-primary hover:underline">← Back to SafetyQR</a>
          <span className="text-border">·</span>
          <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a>
          <span className="text-border">·</span>
          <a href="/grievance" className="text-muted-foreground hover:text-foreground transition-colors">Tracked Support</a>
        </div>
      </div>
    </div>
  )
}

// ─── Local helpers ────────────────────────────────────────────────────────────

function Section({
  number,
  title,
  children,
}: {
  number: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-xl sm:text-2xl leading-snug">
        {number}. {title}
      </h2>
      <div className="text-sm leading-7 text-muted-foreground">{children}</div>
    </section>
  )
}

function Divider() {
  return <hr className="border-border/50" />
}
